import { useIntl } from 'react-intl';
import { BlocksForm, RenderBlocks } from '@plone/volto/components';
// eslint-disable-next-line import/no-unresolved
import { Content } from '@plone/types';
import { ButtonProps } from '@designsystem/stories/02-atoms/Button';
import { MediaProps } from '@designsystem/stories/02-atoms/Media';
import { hasMedia } from '@designsystem/stories/02-atoms/Media.utils';
import { CTA } from '@designsystem/stories/04-organisms/CTA';
import { Editable } from '../../Editable';
import {
  NormalizableContent,
  transformToCard,
} from '@fhnw/helpers/DesignSystem/cardTransformers';
import { normalizeContent } from '@fhnw/helpers/DesignSystem/normalizeContent';
import { RichText } from '@fhnw/components/RichText';
import { messages } from './messages';

export interface BlockDataProps {
  title?: string;
  media?: MediaProps;
  actions?: ButtonProps[];
  contact?: Content;
  blocks?: object;
  blocks_layout?: { items: any[] };
  placeholder?: string;
}

export interface ViewProps {
  data: BlockDataProps;
  isEdit?: boolean;
  block: string;
  pathname?: string;
  onChangeBlock: (blockId: string, newData: any) => void;
  blocksConfig: any;
  manage?: any;
  selectedBlock: string | null;
  selected?: boolean;
  onSelectBlock: (
    blockId: string,
    isMultipleSelection: boolean,
    e:
      | React.MouseEvent<HTMLElement>
      | React.KeyboardEvent<HTMLElement>
      | undefined,
    selectedBlock: string | null,
  ) => void;
  onChangeField?: (id: string, value: any) => void;
  metadata?: any;
  properties?: any;
  multiSelected: string[] | null;
}

const View = (props: ViewProps) => {
  const {
    block,
    data,
    onChangeBlock,
    onChangeField,
    pathname,
    selected,
    selectedBlock,
    onSelectBlock,
    manage,
    blocksConfig,
    isEdit,
    multiSelected,
  } = props;

  const intl = useIntl();
  const metadata = props.metadata || props.properties;
  const contact = data?.contact
    ? transformToCard(data?.contact as NormalizableContent, intl)
    : undefined;

  const contact_title = (contact?.title as string) || '';
  const address = data?.contact
    ? `${data.contact.location_address?.address || ''}\n${data.contact.location_address?.zipcode || ''} ${data.contact.location_address?.city || ''}`
    : '';

  let title: string | JSX.Element = data?.title || '';
  let image = normalizeContent(data?.media, true, {
    '60rem': '100vw',
    '106rem': '50vw',
    max: '53rem',
  }) as MediaProps;
  let actions = normalizeContent(data?.actions, true) as ButtonProps[];
  if (isEdit) {
    title = (
      <Editable
        name="title"
        value={title}
        data={data}
        placeholder={intl.formatMessage(messages.Title)}
        block={block}
        onChangeBlock={onChangeBlock}
      />
    );
  }

  if (
    !hasMedia(image) &&
    hasMedia(contact?.image) &&
    contact?.image?.className !== 'media--placeholder'
  ) {
    image = contact?.image as MediaProps;
  }

  if (!actions || (actions.length === 0 && data?.contact)) {
    actions = [];
    if (data?.contact?.phone1) {
      actions.push({
        type: 'tertiary',
        icon: 'phone',
        label: data.contact.phone1,
        href: `tel:${data.contact.phone1}`,
      });
    }
    if (data?.contact?.email) {
      actions.push({
        type: 'tertiary',
        icon: 'closed_envelope',
        label: data.contact.email,
        href: `mailto:${data.contact.email}`,
      });
    }
  }

  return (
    <CTA title={title} image={image} actions={actions}>
      {data?.contact && (
        <>
          {contact_title && (
            <p>
              <strong>{contact_title}</strong>
              {data?.contact?.role && (
                <>
                  <br />
                  {data.contact.role}
                </>
              )}
            </p>
          )}
          {address.trim() && (
            <p>
              <strong>{intl.formatMessage(messages.Address)}</strong>
              <br />
              <RichText text={address} />
            </p>
          )}
        </>
      )}
      {!isEdit && data?.blocks && data?.blocks_layout?.items && (
        <RenderBlocks content={data} />
      )}
      {isEdit && (
        <BlocksForm
          {...props}
          isMainForm={false}
          metadata={metadata}
          properties={data}
          manage={manage}
          selectedBlock={selected ? selectedBlock : null}
          allowedBlocks={blocksConfig.cta_block.allowedBlocks}
          title={data.placeholder}
          onSelectBlock={(
            id: string,
            l: boolean,
            e:
              | React.MouseEvent<HTMLElement>
              | React.KeyboardEvent<HTMLElement>
              | undefined,
          ) => {
            const isMultipleSelection = e
              ? e.shiftKey || e.ctrlKey || e.metaKey
              : false;
            onSelectBlock(id, isMultipleSelection, e, selectedBlock);
          }}
          onChangeFormData={(newFormData: BlockDataProps) => {
            onChangeBlock(block, {
              ...data,
              ...newFormData,
            });
          }}
          onChangeField={(id: string, value: any) => {
            if (['blocks', 'blocks_layout'].indexOf(id) > -1) {
              onChangeBlock(block, {
                ...data,
                ...value,
              });
            } else {
              onChangeField && onChangeField(id, value);
            }
          }}
          pathname={pathname}
          multiSelected={multiSelected}
        />
      )}
    </CTA>
  );
};

export default View;
